import { Fragment, useMemo, useState } from 'react';
import { ImageShape, VideoShape } from '@/types';
import { useMediaQuery } from 'usehooks-ts';
import Image from 'next/image';
import Responsive from '../Responsive';
import { useLazySwiper } from '@/hooks/use-swiper-lazy';
import { useSetOpenedPopupsState } from '@/atoms/opened-popups';
import ImagePopup, { IMAGE_POPUP_NAME } from '@/components/general/ImagePopup/ImagePopup';
import VideoDetailCard from '../VideoDetailCard';
import { SwiperClass } from 'swiper/react';
import dynamic from 'next/dynamic';
import { useMediaQueryDeviceState } from '@/atoms/media-query-device';

const ProductVideoPopup = dynamic(() => import('@/components/general/ProductVideoPopup'), {
    ssr: false,
});

interface Props {
    images: ImageShape[];
    video?: VideoShape;
    videos?: VideoShape[];
    onDownloadVideo?: (video: VideoShape | null) => void;
}

const thumbsPerView = 4;

const ProductSlider = ({ images, video, videos, onDownloadVideo }: Props) => {
    const { Swiper, SwiperSlide, swiperModules } = useLazySwiper({ useSwiper: true });
    const Thumbs = swiperModules.find((item) => item.name === 'Thumb');
    const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);
    const thumbsCount = Math.min(images.length, thumbsPerView);
    const isLaptop = useMediaQuery('(max-width: 1799px) and (min-width: 1200px)');
    const isDesktop = useMediaQuery('(min-width: 1800px)');
    const [selectedImage, setSelectedImage] = useState<ImageShape | null>(images[0]);
    const thumbSize = isDesktop ? 72 : isLaptop ? 60 : 52;
    const thumbGap = isDesktop || isLaptop ? 12 : 10;
    const [mediaQueryDevice] = useMediaQueryDeviceState();
    const { openPopup } = useSetOpenedPopupsState();

    const [activeIndex, setActiveIndex] = useState(0);
    const [activeVideo, setActiveVideo] = useState<VideoShape | null>(null);

    const allVideos = useMemo(() => {
        return videos && videos.length > 0 ? videos : video && video.length > 0 ? [video] : [];
    }, [video, videos]);

    const allSlides = useMemo(() => {
        return [
            { type: 'image', data: images[0] },
            ...allVideos.map((video) => ({ type: 'video', data: video })),
            ...images.slice(1).map((img) => ({ type: 'image', data: img })),
        ];
    }, [allVideos, images]);

    const handleSlideChange = (swiper: SwiperClass) => {
        const activeIndex = swiper.realIndex;
        const activeSlide = allSlides[activeIndex];
        setActiveIndex(activeIndex);

        if (activeSlide.type === 'video') {
            setActiveVideo(activeSlide.data as VideoShape);
            onDownloadVideo?.(activeSlide.data as VideoShape);
        } else {
            setActiveVideo(null);
            onDownloadVideo?.(null);
        }
    };

    const sliderZoom = (image: ImageShape) => {
        setSelectedImage(image);
        openPopup(IMAGE_POPUP_NAME);
    };

    return (
        <div className="product-slider">
            <div
                className="product-slider-thumbs"
                style={{
                    height: `${thumbsCount * thumbSize + (thumbsCount - 1) * thumbGap * 2}px`,
                }}
            >
                <Swiper
                    modules={Thumbs ? [Thumbs] : []}
                    watchSlidesProgress
                    onSwiper={setThumbsSwiper}
                    direction="vertical"
                    slidesPerView={thumbsCount}
                    spaceBetween={thumbGap}
                    freeMode={true}
                >
                    {images.map((item, i) => {
                        return (
                            <Fragment key={`${i}-fragment`}>
                                <SwiperSlide className="thumbs-slide" key={i}>
                                    <span className="mustache mustache-left" />
                                    <Image
                                        src={item.src}
                                        width={thumbSize}
                                        height={thumbSize}
                                        alt=""
                                        className="thumbs-slide__image"
                                    />
                                    <span className="mustache mustache-right" />
                                </SwiperSlide>
                                {allVideos &&
                                    allVideos.length > 0 &&
                                    i === 0 &&
                                    allVideos.map((video, index) => (
                                        <SwiperSlide className="thumbs-slide" key={index}>
                                            <span className="mustache mustache-left" />
                                            <video
                                                playsInline
                                                muted
                                                autoPlay
                                                width={thumbSize}
                                                height={thumbSize}
                                                loop
                                                className="thumbs-slide__video"
                                            >
                                                {video.map((item, j) => {
                                                    return (
                                                        <source
                                                            key={j}
                                                            type={
                                                                item.type === 'video/quicktime'
                                                                    ? 'video/mp4'
                                                                    : item.type
                                                            }
                                                            src={item.src}
                                                        />
                                                    );
                                                })}
                                            </video>
                                            <span className="mustache mustache-right" />
                                        </SwiperSlide>
                                    ))}
                            </Fragment>
                        );
                    })}
                </Swiper>
            </div>
            <div className="product-slider-main-wrapper">
                <Responsive className="product-slider-main-responsive">
                    <Swiper
                        modules={Thumbs ? [Thumbs] : []}
                        thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                        direction="vertical"
                        className="product-slider-main"
                        spaceBetween={14}
                        grabCursor
                        onSlideChange={handleSlideChange}
                    >
                        {images.map((item, i) => {
                            return (
                                <Fragment key={`${i}-fragment`}>
                                    <SwiperSlide
                                        key={i}
                                        className="product-slide-main"
                                        onClick={() => sliderZoom(item)}
                                    >
                                        <Image
                                            src={item.src}
                                            width={item.width}
                                            height={item.height}
                                            alt=""
                                            className="product-slide-main-image"
                                            priority={i === 0}
                                        />
                                    </SwiperSlide>
                                    {allVideos &&
                                        allVideos.length > 0 &&
                                        i === 0 &&
                                        allVideos.map((video, index) => (
                                            <SwiperSlide key={index}>
                                                <VideoDetailCard
                                                    video={video}
                                                    isVideoZoom
                                                    isActive={activeIndex === index + 1}
                                                />
                                            </SwiperSlide>
                                        ))}
                                </Fragment>
                            );
                        })}
                    </Swiper>
                </Responsive>
            </div>

            {selectedImage && <ImagePopup item={selectedImage} />}
            {activeVideo && <ProductVideoPopup key={activeVideo[0].src} video={activeVideo} />}
        </div>
    );
};

export default ProductSlider;
